import { useState, useEffect, useRef, useCallback } from 'react';
import { websocketUrl as getWebsocketUrl } from '../lib/api';

interface WebSocketMessage {
  type: 'ping' | 'pong' | 'userCount';
  count?: number;
  timestamp?: number;
}

interface UseOnlineUsersResult {
  onlineUsers: number;
  isConnected: boolean;
  error: string | null;
  reconnect: () => void;
}

export const useOnlineUsers = (
  wsUrlOverride?: string
): UseOnlineUsersResult => {
  const [onlineUsers, setOnlineUsers] = useState<number>(0);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  const ws = useRef<WebSocket | null>(null);
  const reconnectTimeout = useRef<number | null>(null);
  const pingInterval = useRef<number | null>(null);
  const reconnectAttempts = useRef<number>(0);
  
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000;
  const pingIntervalTime = 30000; // 30 seconds

  const cleanup = useCallback(() => {
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
      reconnectTimeout.current = null;
    }
    if (pingInterval.current) {
      clearInterval(pingInterval.current);
      pingInterval.current = null;
    }
    if (ws.current) {
      ws.current.close();
      ws.current = null;
    }
  }, []);

  const startPingInterval = useCallback(() => {
    if (pingInterval.current) {
      clearInterval(pingInterval.current);
    }
    
    pingInterval.current = setInterval(() => {
      if (ws.current && ws.current.readyState === WebSocket.OPEN) {
        ws.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, pingIntervalTime);
  }, []);

  const connect = useCallback(() => {
    // Use configured WebSocket URL if not provided
    const url = wsUrlOverride || getWebsocketUrl();
    
    cleanup();
    
    try {
      ws.current = new WebSocket(url);
      
      ws.current.onopen = () => {
        setIsConnected(true);
        setError(null);
        reconnectAttempts.current = 0;
        startPingInterval();
      };
      
      ws.current.onmessage = (event) => {
        try {
          const data: WebSocketMessage = JSON.parse(event.data);
          
          if (data.type === 'userCount' && typeof data.count === 'number') {
            setOnlineUsers(data.count);
          }
          
          // Handle pong responses (optional)
          if (data.type === 'pong') {
            // Connection is alive
          }
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };
      
      ws.current.onclose = () => {
        setIsConnected(false);
        if (pingInterval.current) {
          clearInterval(pingInterval.current);
          pingInterval.current = null;
        }
        
        // Attempt to reconnect if we haven't exceeded max attempts
        if (reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current++;
          setError(`Connection lost. Reconnecting... (${reconnectAttempts.current}/${maxReconnectAttempts})`);
          
          reconnectTimeout.current = setTimeout(() => {
            connect();
          }, reconnectDelay);
        } else {
          setError('Connection failed. Maximum reconnection attempts reached.');
        }
      };
      
      ws.current.onerror = (event) => {
        console.error('WebSocket error:', event);
        setError('WebSocket connection error');
      };
      
    } catch (err) {
      setError('Failed to create WebSocket connection');
      console.error('WebSocket creation error:', err);
    }
  }, [wsUrlOverride, cleanup, startPingInterval]);

  const reconnect = useCallback(() => {
    reconnectAttempts.current = 0;
    setError(null);
    connect();
  }, [connect]);

  useEffect(() => {
    connect();
    
    return () => {
      cleanup();
    };
  }, [connect, cleanup]);

  return {
    onlineUsers,
    isConnected,
    error,
    reconnect
  };
}; 